import { z } from 'zod';
import { CommentableTypeSchema, commentableRecordPath } from '@erp/rpc';

/**
 * Deep-link to a workflow run timeline. Both ids are required because the
 * run-detail route is nested under the workflow lineage.
 */
export function workflowRunHref(
  workflowId: string | null | undefined,
  runId: string | null | undefined,
): string | null {
  if (!workflowId || !runId) return null;
  return `/workflows/${workflowId}/runs/${runId}`;
}

const WORKFLOW_RUN_HREF = /^\/workflows\/[^/]+\/runs\/[^/]+$/;

export function isWorkflowRunHref(href: string): boolean {
  return WORKFLOW_RUN_HREF.test(href);
}

const nonEmptyString = z.string().min(1);

/** Read a non-empty string field from a loosely typed notification payload. */
export function payloadString(
  payload: Record<string, unknown>,
  key: string,
): string | null {
  const parsed = nonEmptyString.safeParse(payload[key]);
  return parsed.success ? parsed.data : null;
}

/**
 * Build a run href from a notification payload. Tries `runIdKeys` in order,
 * then `fallbackRunId` (typically `notifiableId` when the notifiable is a
 * WorkflowRun).
 */
export function workflowRunHrefFromPayload(
  payload: Record<string, unknown>,
  runIdKeys: readonly string[],
  fallbackRunId?: string | null,
): string | null {
  const workflowId = payloadString(payload, 'workflowId');
  for (const key of runIdKeys) {
    const runId = payloadString(payload, key);
    if (runId) return workflowRunHref(workflowId, runId);
  }
  return workflowRunHref(workflowId, fallbackRunId);
}

/**
 * Deep link to the record a note was left on, where its notes panel lives
 * (the same path the mention email uses). Null for a type this build does
 * not know — an older client reading a notification about a newly
 * commentable record.
 */
export function commentableRecordHref(
  commentableType: string | null,
  commentableId: string | null,
): string | null {
  const parsed = CommentableTypeSchema.safeParse(commentableType);
  if (!parsed.success || !commentableId) return null;
  return commentableRecordPath(parsed.data, commentableId);
}
